import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Section } from "@/components/design/Section";
import {
  fetchFilterOptions,
  fetchTalents,
  TobisNotConfiguredError,
  type FilterOptions,
  type TobisTalentFilters,
  type TobisTalentSort,
} from "@/lib/tobis";
import { isRealUpdateTimestamp } from "@/lib/tobis-freshness";
import { logApplicationFailure } from "@/lib/user-error";
import { useTobisTalentStore } from "@/stores/tobis-talent-store";
import { TobisPageShell } from "./TobisPageShell";
import { TobisTalentFilterBar } from "./TobisFilters";
import { TobisPersonRow } from "./TobisPersonRow";

type TobisView = "grid" | "compact";

// Register der Schauspieler. Sortiert nach Anzahl der Rollen, weil eine
// Filmografie erst mit mehreren Rollen aussagekraeftig wird.

export function TobisTalentsPage() {
  const intl = useIntl();
  // Filter, Sortierung und die geladene Liste liegen im Store, damit ein
  // Zurueck von einer Personenseite nicht neu laedt (siehe tobis-talent-store,
  // dasselbe Prinzip wie beim Titel-Register).
  const { filters, sort, talents, total, loaded, setFilters, setSort, setPage, markLoaded } =
    useTobisTalentStore();
  const [options, setOptions] = useState<FilterOptions | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "notConfigured" | "error">(
    loaded ? "ready" : "loading",
  );
  const [view, setView] = useState<TobisView>("grid");

  // Wie bei der Titel-Liste: verhindert, dass eine AELTERE Antwort nach einer
  // neueren zurueckkommt und das frische Ergebnis mit einem veralteten
  // ueberschreibt (schnelles Tippen, oder der erste Aufruf ist noch unterwegs).
  const latestRequestRef = useRef(0);

  const load = useCallback(
    async (active: TobisTalentFilters, activeSort: TobisTalentSort) => {
      const requestId = ++latestRequestRef.current;
      setPhase("loading");
      try {
        const page = await fetchTalents(active, 100, activeSort);
        if (requestId !== latestRequestRef.current) return;
        setPage(page.talents, page.total);
        setPhase("ready");
      } catch (err) {
        if (requestId !== latestRequestRef.current) return;
        markLoaded();
        if (err instanceof TobisNotConfiguredError) {
          setPhase("notConfigured");
          return;
        }
        logApplicationFailure("tobis.talents");
        setPhase("error");
      }
    },
    [setPage, markLoaded],
  );

  useEffect(() => {
    void fetchFilterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    if (loaded) return;
    const timer = window.setTimeout(() => void load(filters, sort), 250);
    return () => window.clearTimeout(timer);
  }, [filters, sort, loaded, load]);

  const heading = intl.formatMessage({ id: "tobis.people.heading", defaultMessage: "People" });

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={heading}
      intro={intl.formatMessage({
        id: "tobis.people.intro",
        defaultMessage:
          "Actors from the loaded cast lists. Open a name to see the filmography as a timeline.",
      })}
    >
      {phase === "notConfigured" || phase === "error" ? (
        <Section title={heading}>
          <p className="text-sm text-ink/55">
            {phase === "notConfigured"
              ? intl.formatMessage({
                  id: "tobis.notConfigured",
                  defaultMessage:
                    "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
                })
              : intl.formatMessage({
                  id: "tobis.loadError",
                  defaultMessage: "The overview could not be loaded.",
                })}
          </p>
        </Section>
      ) : (
        // Filterleiste liegt bewusst ausserhalb der Section, aus demselben
        // Grund wie auf der Titel-Seite: bei 0 Treffern ersetzt Section den
        // Children-Block komplett, sonst waere die Filterleiste mit weg und
        // der Nutzer koennte den Filter nicht mehr loesen.
        <div className="space-y-5">
          <TobisTalentFilterBar
            filters={filters}
            onChange={setFilters}
            options={options}
            sort={sort}
            onSortChange={setSort}
          />

          <Section
            title={heading}
            right={
              <div className="flex items-center gap-3">
                <SegmentedControl
                  value={view}
                  onChange={setView}
                  size="sm"
                  options={[
                    {
                      value: "grid",
                      label: intl.formatMessage({ id: "tobis.view.grid", defaultMessage: "Tiles" }),
                    },
                    {
                      value: "compact",
                      label: intl.formatMessage({ id: "tobis.view.compact", defaultMessage: "Compact" }),
                    },
                  ]}
                />
                <span className="text-[12px] text-ink/40">
                  {total !== null && total > talents.length
                    ? intl.formatMessage(
                        { id: "tobis.people.countOfTotal", defaultMessage: "{count} of {total} people loaded" },
                        { count: intl.formatNumber(talents.length), total: intl.formatNumber(total) },
                      )
                    : intl.formatMessage(
                        { id: "tobis.people.count", defaultMessage: "{count} people" },
                        { count: intl.formatNumber(talents.length) },
                      )}
                </span>
              </div>
            }
            empty={phase === "ready" && talents.length === 0}
            emptyLabel={intl.formatMessage({
              id: "tobis.people.empty",
              defaultMessage: "No people loaded yet.",
            })}
            emptyHint={intl.formatMessage({
              id: "tobis.people.emptyHint",
              defaultMessage: "Cast lists arrive with a TVmaze fetch.",
            })}
          >
            {phase === "loading" ? (
              // Eigener Zwischenzustand statt einer leeren Kachelliste - sonst
              // sieht eine laufende Suche (Debounce + Serverantwort) identisch
              // zu "0 Treffer" aus, siehe TobisTitlesPage.
              <div
                role="status"
                aria-live="polite"
                className="flex items-center justify-center gap-2 rounded-md bg-ink/[0.02] px-4 py-6 ring-1 ring-ink/15"
              >
                <span
                  aria-hidden
                  className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent text-ink/40"
                />
                <span className="text-[12px] text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.people.loading",
                    defaultMessage: "Searching the database…",
                  })}
                </span>
              </div>
            ) : (
              // Grid: wie in der erweiterten Suche, Personen in bis zu vier
              // Spalten. Compact: eine bis zwei Spalten mit kleinerem Avatar
              // und weniger Padding, fuer zuegiges Durchscrollen.
              <div
                className={
                  view === "grid"
                    ? "grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
                    : "grid grid-cols-1 gap-2 @lg:grid-cols-2"
                }
              >
                {talents.map((p) => (
                  <TobisPersonRow
                    key={p.personId}
                    name={p.name}
                    photoUrl={p.photoUrl}
                    title={p.name ?? ""}
                    dense={view === "compact"}
                    sub={[
                      // Vorangestellt wie in der Titelkarte: nur bei der
                      // Aktualitaets-Sortierung gesetzt, und dann die Angabe,
                      // wegen der so sortiert wurde.
                      isRealUpdateTimestamp(p.updatedAt ?? undefined)
                        ? intl.formatDate(new Date(p.updatedAt as string), {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : null,
                      intl.formatMessage(
                        { id: "tobis.people.credits", defaultMessage: "{count} roles" },
                        { count: p.creditCount },
                      ),
                      p.country,
                      p.birthday ? p.birthday.slice(0, 4) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    to={`/tobis/people/${p.personId}`}
                  />
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </TobisPageShell>
  );
}
